import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesEnum } from '~/constants/enums';
import { useFullRoutes } from '~/hooks';
import FeatureCard from '~/pages/FeatureListing/FeatureCard';
import FeatureConfirmModal from '~/pages/FeatureListing/FeatureConfirmModal';
import FeatureErrorBanner from '~/pages/FeatureListing/FeatureErrorBanner';
import FeatureListingLoading from '~/pages/FeatureListing/FeatureListingLoading';
import FeatureNoCreditModal from '~/pages/FeatureListing/FeatureNoCreditModal';
import {
  buildListingFeatures,
  markListingFeatured,
} from '~/pages/FeatureListing/utils';
import {
  useFeatureListingMutation,
  useGetFeaturedListingRulesQuery,
} from '~/redux/api/featureListingApiSlice';
import {
  useGetListingDetailsQuery,
  useListingStatusQuery,
} from '~/redux/api/userApiSlice';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import { ListingFeatureType } from '~/types/featureListingTypes';
import '~/pages/FeatureListing/styles.css';

export default function FeatureListing() {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { ADD_LISTING_URL } = useFullRoutes();
  const [selectedFeature, setSelectedFeature] =
    useState<ListingFeatureType | null>(null);
  const [hasError, setHasError] = useState(false);
  const {
    data,
    isLoading,
    isError: isRulesError,
  } = useGetFeaturedListingRulesQuery(
    { id: id as string, locale },
    { skip: !id },
  );
  const { data: listingStatusData, isLoading: isListingStatusLoading } =
    useListingStatusQuery(id, { skip: !id });
  const { data: listingDetailsData } = useGetListingDetailsQuery(id, {
    skip: !id,
  });
  const [featureListing, { isLoading: isFeaturing }] =
    useFeatureListingMutation();
  const listingFeatures = buildListingFeatures(data);

  const slug = listingDetailsData?.listing?.section?.slug;
  const listingDetailUrl = `${process.env.REACT_APP_BASE_URL}/${locale}/${RoutesEnum.LISTING}/${id}${slug ? `-${slug}` : ''}`;

  const isPayAsYouGo = Boolean(
    listingStatusData?.isPayAsYouGoAllowed &&
    !listingStatusData?.isEligibleForFreePublish,
  );

  useEffect(() => {
    document.title = `${t('featureListingPageTitle')}|${t('aqarmap')}`;
  }, [t, locale]);

  useEffect(() => {
    if (isRulesError) setHasError(true);
  }, [isRulesError]);

  useEffect(() => {
    if (!hasError) return;
    const timer = setTimeout(() => setHasError(false), 10000);
    return () => clearTimeout(timer);
  }, [hasError]);

  const handleSelect = (feature: ListingFeatureType) => {
    if (isListingStatusLoading) return;

    setHasError(false);
    setSelectedFeature(feature);

    if (isPayAsYouGo) {
      navigate(ADD_LISTING_URL.FEATURE_CHECKOUT(`${id}`, feature.key));
      return;
    }
    const hasCreditToFeature =
      Number(feature.fees) >
      Number(listingStatusData?.userAvailableCreditBalance)
        ? false
        : true;

    dispatch(
      openModal(
        hasCreditToFeature
          ? 'featureListingConfirmModal'
          : 'featureListingNoCreditModal',
      ),
    );
  };

  const handleConfirm = async () => {
    if (!id || !selectedFeature) return;

    setHasError(false);
    const result = await featureListing({
      id,
      locale,
      body: {
        listingFeaturedType: selectedFeature.listingFeaturedType,
        listingFeature: selectedFeature.listingFeature,
      },
    });

    dispatch(closeModal('featureListingConfirmModal'));
    if ('error' in result) {
      setHasError(true);
      return;
    }

    markListingFeatured({ id, costType: 'points', feature: selectedFeature });
    window.location.href = listingDetailUrl;
  };

  return (
    <div className="flex w-full flex-col">
      {hasError ? (
        <FeatureErrorBanner onClose={() => setHasError(false)} />
      ) : null}
      <div className="layout-boundary mb-6x pt-3x md:pt-4x md:mb-10x">
        <div className="layout-boundary__inner flex w-full flex-col items-center gap-3x md:gap-4x px-2x md:px-0">
          <p className="w-full text-center text-title-3 md:text-title-2">
            {t('featureListingTitle')}
          </p>
          {isLoading || isListingStatusLoading ? (
            <FeatureListingLoading />
          ) : (
            <div className="feature-listing-grid">
              {listingFeatures.map((feature) => (
                <div className="feature-listing-card-item" key={feature.key}>
                  <FeatureCard
                    feature={feature}
                    isPayAsYouGo={isPayAsYouGo}
                    onSelect={handleSelect}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <FeatureConfirmModal
        feature={selectedFeature}
        isLoading={isFeaturing}
        onConfirm={handleConfirm}
      />
      <FeatureNoCreditModal feature={selectedFeature} />
    </div>
  );
}
